import { useCallback, useMemo, useState } from 'react';
import ArchiveLeadsModal from 'src/components/AchiveLeadsModal/ArchiveLeads.modal';
import AdvanceSearchModal from '../../components/AdvanceSearch/AdvanceSearchModal.component.tsx';
import ModalPresence from '../../components/ModalPresence/ModalPresence.component.tsx';
import { AddWorkspaceModal } from '../../components/Sidebar/components/Modals/AddWorkspaceModal/AddWorkspaceModal.component';
import ConnectionModal from '../../components/Sidebar/components/Modals/ConnectionModal/ConnectionModal.component.tsx';
import DisconnectionModal from '../../components/Sidebar/components/Modals/DisconnectionModal/DisconnectionModal.component.tsx';
import ForceDisconnectionModal from '../../components/Sidebar/components/Modals/ForceDisconnectionModal/ForceDisconnectionModal.component.tsx';
import MetaConnectionModal from '../../pages/Dashboard/components/Modals/MetaConnectionModal/MetaConnectionModal.component.tsx';
import MetaConnectionModalProvider from '../../pages/Dashboard/components/Modals/MetaConnectionModal/MetaConnectionModal.context.tsx';
import WhatsAppBusinessConnectionModal from '../../pages/Dashboard/components/Modals/WhatsAppBusinessConnectionModal/WhatsAppBusinessConnectionModal.component.tsx';
import SyncConversationsQuestion from '../../pages/Dashboard/components/Modals/SyncConversations/SyncConversationsQuestion/SyncConversationsQuestion.component.tsx';
import SyncConversationsSelect from '../../pages/Dashboard/components/Modals/SyncConversations/SyncConversationsSelect/SyncConversationsSelect.component.tsx';
import SyncConversationsSuccess from '../../pages/Dashboard/components/Modals/SyncConversations/SyncConversationsSuccess/SyncConversationsSuccess.component.tsx';
import SyncConversationsSyncing from '../../pages/Dashboard/components/Modals/SyncConversations/SyncConversationsSyncing/SyncConversationsSyncing.component.tsx';
import SyncConversationsToaster from '../../pages/Dashboard/components/Modals/SyncConversations/SyncConversationsToaster/SyncConversationsToaster.component.tsx';
import DeleteFunnelProgressModal from '../../pages/Pipe/modals/DeleteFunnelProgressModal/DeleteFunnelProgressModal.component.tsx';
import { eDeleteFunnelProgressModal } from '../../pages/Pipe/modals/DeleteFunnelProgressModal/DeleteFunnelProgressModal.enum.ts';
import DeleteFunnelProgressToast from '../../pages/Pipe/modals/DeleteFunnelProgressModal/DeleteFunnelProgressToast.component.tsx';
import NoPaidModal from '../SubscriptionProvider/components/Modals/NoPaidModal.component.tsx';
import PastDueModal from '../SubscriptionProvider/components/Modals/PastDueModal.component.tsx';
import PrevPastDueModal from '../SubscriptionProvider/components/Modals/PrevPastDueModal.component.tsx';
import ShowBankDetailsModal from '../SubscriptionProvider/components/Modals/ShowBankDetailsModal.component.tsx';
import SubscribeImportLeadsModal from '../SubscriptionProvider/components/Modals/SubscribeImportLeads.component.tsx';
import SubscribeIntegrationsModal from '../SubscriptionProvider/components/Modals/SubscribeIntegrationsModal.component.tsx';
import SubscribeUsersModal from '../SubscriptionProvider/components/Modals/SubscribeUsersModal.component.tsx';
import { SyncConversationsEnum } from '../SyncConversationsProvider/SyncConversations.enum.ts';
import WhatsAppConnectionWizard from 'src/pages/Dashboard/components/Modals/WhatsAppConnectionWizard/WhatsAppConnectionWizard.component';
import { WhatsAppConnectionWizardProvider } from 'src/pages/Dashboard/components/Modals/WhatsAppConnectionWizard/WhatsAppConnectionWizard.provider';
import WhatsAppConnectionWizardSimulator from 'src/pages/Dashboard/components/Modals/WhatsAppConnectionWizard/WhatsAppConnectionWizardSimulator.component';
import { IS_WAPI_WIZARD_SIMULATOR_ENABLED } from 'src/constants/envs';
import { useLoadedFeatureFlag } from 'src/hooks/featureFlags/useLoadedFeatureFlag.hook';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { ModalsContext } from './Modals.context';
import useModals from './Modals.hook';
import { tModalsProviderProps } from './Modals.types';

export const ModalsProvider = (props: tModalsProviderProps) => {
  const { children } = props;
  const modals = useModals();
  // A flag map that has not loaded reads as off, so the current modal shows
  // until this workspace's flags settle.
  const isWhatsAppConnectionWizardEnabled = useLoadedFeatureFlag(
    'integrations_wapi-connection-wizard',
  );

  // A workspace the wizard connected in this page session gets the current
  // modal from then on, even while its flag still reads on. The last defined
  // workspace id answers while the id is unknown for a moment; another id
  // starts with nothing connected.
  const { currentWorkspaceId } = useWorkspaceContext();
  const [lastWorkspaceId, setLastWorkspaceId] = useState(currentWorkspaceId);
  if (currentWorkspaceId && currentWorkspaceId !== lastWorkspaceId) {
    setLastWorkspaceId(currentWorkspaceId);
  }
  const [connectedWorkspaceIds, setConnectedWorkspaceIds] = useState<string[]>(
    [],
  );
  const handleWizardConnected = useCallback((workspaceId: string) => {
    setConnectedWorkspaceIds((workspaceIds) =>
      workspaceIds.includes(workspaceId)
        ? workspaceIds
        : [...workspaceIds, workspaceId],
    );
  }, []);
  const isConnectedInSession =
    lastWorkspaceId !== undefined &&
    connectedWorkspaceIds.includes(lastWorkspaceId);
  // Read when the modal opens and kept until the next opening, so the wizard
  // stays on its connected screen, and leaves through its own exit, instead of
  // turning into the current modal under the person. The flag is read the same
  // way: flags that load or change while the modal is open would otherwise
  // swap one modal for the other, and both would count the opening. Leaving
  // the wizard for the current modal holds for this opening only.
  const [connectionModalOpening, setConnectionModalOpening] = useState({
    isOpen: modals.whatsAppBusinessConnectionModalIsOpen,
    isConnectedInSession,
    isWizardEnabled: isWhatsAppConnectionWizardEnabled,
    isLegacyChosen: false,
  });
  if (
    connectionModalOpening.isOpen !==
    modals.whatsAppBusinessConnectionModalIsOpen
  ) {
    setConnectionModalOpening({
      isOpen: modals.whatsAppBusinessConnectionModalIsOpen,
      isConnectedInSession: modals.whatsAppBusinessConnectionModalIsOpen
        ? isConnectedInSession
        : connectionModalOpening.isConnectedInSession,
      isWizardEnabled: isWhatsAppConnectionWizardEnabled,
      isLegacyChosen: false,
    });
  }
  // The modal stays open, so the attempt keeps its source and onboarding does
  // not read the switch as a close.
  const handleLegacyChosen = useCallback(() => {
    setConnectionModalOpening((opening) => ({
      ...opening,
      isLegacyChosen: true,
    }));
  }, []);
  // While closed the flag answers as it reads, so the wizard's state provider
  // follows it and the next opening latches its fresh value.
  const isWizardEnabled = modals.whatsAppBusinessConnectionModalIsOpen
    ? connectionModalOpening.isWizardEnabled
    : isWhatsAppConnectionWizardEnabled;
  const isWizardShown =
    isWizardEnabled &&
    !connectionModalOpening.isConnectedInSession &&
    !connectionModalOpening.isLegacyChosen;
  const modalsContextValue = useMemo(
    () => ({
      ...modals,
      whatsAppBusinessConnectionModalIsLegacyChosen:
        connectionModalOpening.isLegacyChosen,
    }),
    [modals, connectionModalOpening.isLegacyChosen],
  );

  const content = (
    <>
      {/* Each modal reads the same flag for its own `open`: ModalPresence keeps
          it mounted for its exit, and a modal that replaces another crosses
          with it (DES-222). The wizard and the current modal are siblings, so
          one leaves while the other enters. */}
      <ModalPresence open={modals.addWorkspaceModelIsOpen}>
        <AddWorkspaceModal />
      </ModalPresence>
      <ModalPresence open={modals.connectionModalIsOpen}>
        <ConnectionModal />
      </ModalPresence>
      <ModalPresence open={modals.disconnectionModalIsOpen}>
        <DisconnectionModal />
      </ModalPresence>
      <ModalPresence open={modals.forceDisconnectionModalIsOpen}>
        <ForceDisconnectionModal />
      </ModalPresence>
      <ModalPresence open={modals.metaConnectionModalIsOpen}>
        <MetaConnectionModalProvider>
          <MetaConnectionModal />
        </MetaConnectionModalProvider>
      </ModalPresence>
      <ModalPresence
        open={modals.whatsAppBusinessConnectionModalIsOpen && isWizardShown}
      >
        <WhatsAppConnectionWizard />
      </ModalPresence>
      <ModalPresence
        open={modals.whatsAppBusinessConnectionModalIsOpen && !isWizardShown}
      >
        <WhatsAppBusinessConnectionModal />
      </ModalPresence>
      {/* A sibling of the wizard's ModalPresence, not inside it, so it stays
          mounted while the modal is closed and can open it. */}
      {isWhatsAppConnectionWizardEnabled && IS_WAPI_WIZARD_SIMULATOR_ENABLED ? (
        <WhatsAppConnectionWizardSimulator />
      ) : null}
      <ModalPresence
        open={modals.syncConversationsStatus === SyncConversationsEnum.QUESTION}
      >
        <SyncConversationsQuestion />
      </ModalPresence>
      <ModalPresence
        open={
          modals.syncConversationsStatus === SyncConversationsEnum.SELECTING
        }
      >
        <SyncConversationsSelect />
      </ModalPresence>
      <ModalPresence
        open={modals.syncConversationsStatus === SyncConversationsEnum.SYNCING}
      >
        <SyncConversationsSyncing />
      </ModalPresence>
      <ModalPresence
        open={modals.syncConversationsStatus === SyncConversationsEnum.SUCCESS}
      >
        <SyncConversationsSuccess />
      </ModalPresence>
      {modals.syncConversationsStatus ===
        SyncConversationsEnum.PROGRESS_TOASTER && <SyncConversationsToaster />}
      <ModalPresence open={modals.showPastDueModal}>
        <PastDueModal />
      </ModalPresence>
      <ModalPresence open={modals.showNoPaidModal}>
        <NoPaidModal />
      </ModalPresence>
      <ModalPresence open={modals.showPrevPastDueModal}>
        <PrevPastDueModal />
      </ModalPresence>
      <ModalPresence open={modals.showBankDetailsModal}>
        <ShowBankDetailsModal />
      </ModalPresence>
      <ModalPresence open={modals.advanceSearchModalIsOpen}>
        <AdvanceSearchModal />
      </ModalPresence>
      <ModalPresence open={modals.showSubscribeIntegrationsModal}>
        <SubscribeIntegrationsModal />
      </ModalPresence>
      <ModalPresence open={modals.showSubscribeAddUsersModal}>
        <SubscribeUsersModal />
      </ModalPresence>
      <ModalPresence open={modals.showSubscribeImportLeadsModal}>
        <SubscribeImportLeadsModal />
      </ModalPresence>
      <ModalPresence
        open={
          modals.showDeleteFunnelModal === eDeleteFunnelProgressModal.SUCCESS
        }
      >
        <DeleteFunnelProgressModal />
      </ModalPresence>
      {modals.showDeleteFunnelModal === eDeleteFunnelProgressModal.PROGRESS && (
        <DeleteFunnelProgressToast />
      )}
      <ModalPresence open={modals.showArchiveLeadsModal}>
        <ArchiveLeadsModal />
      </ModalPresence>
      {children}
    </>
  );

  // The wizard's state provider sits outside ModalPresence so a connection
  // keeps going while the wizard is closed, and around the page so the page
  // (the integrations card) reads the same connection. It is always rendered
  // and inert while the flag is off or unloaded, so the flags settling never
  // remounts the modals or the page.
  return (
    <ModalsContext.Provider value={modalsContextValue}>
      <WhatsAppConnectionWizardProvider
        isEnabled={isWizardEnabled}
        isWizardShown={isWizardShown}
        onConnected={handleWizardConnected}
        onLegacyChosen={handleLegacyChosen}
      >
        {content}
      </WhatsAppConnectionWizardProvider>
    </ModalsContext.Provider>
  );
};
